import { useState, useEffect } from 'react';
import Image from 'next/image';
import { ImageProps } from 'next/image';
import { Image as TImage, ImageFieldName } from 'src/types';
import { getLocalFullUrl } from 'src/helpers';
import imgDefault from 'public/images/default.jpeg';

const ERROR_COUNT_TO_QUALITY: { [key: number]: number } = {
  1: 85,
  2: 95,
  3: 100
}

const ImageWithFallback: <T extends TImage | undefined>(
  props: Omit<ImageProps, 'src'> & {
    record?: T;
    portraitClassName?: string;
  } & (T extends TImage
      ? {
          src?: ImageProps['src'];
          smSrc?: ImageProps['src'] | undefined | null;
          imgField: ImageFieldName;
          smImgField?: ImageFieldName;
        }
      : {
          src: ImageProps['src'] | undefined | null;
          smSrc?: ImageProps['src'] | undefined | null;
          imgField?: undefined;
          smImgField?: undefined;
        })
) => JSX.Element = ({ portraitClassName, record, imgField, smImgField, src, smSrc, ...nextProps }) => {
  const [errorCount, setErrorCount] = useState(0);
  const [isPortrait, setIsPortrait] = useState(false);
  const unoptimized = typeof nextProps.unoptimized === 'boolean' ? nextProps.unoptimized : record ? true : false
  const safeImgField = 'url' as ImageFieldName

  const actualSrc = errorCount > 0 && !ERROR_COUNT_TO_QUALITY[errorCount]
    ? imgDefault
    : (record && imgField ? record[imgField] : src) || '';

  const actualSmSrc = (record && smImgField ? record[smImgField] : smSrc) || '';
  const actualSmSrcStr = (typeof actualSmSrc === 'string') ? actualSmSrc : 'src' in actualSmSrc ? actualSmSrc.src : undefined

  useEffect(() => {
    setErrorCount(0)
  }, [src])

  return (
    <picture>
      {actualSmSrcStr ? (
        <source media="(max-width: 768px)" srcSet={actualSmSrcStr} />
      ) : null}
      <Image
        {...nextProps}
        className={isPortrait ? (portraitClassName || nextProps.className) : nextProps.className}
        src={actualSrc}
        alt={nextProps.alt}
        placeholder="empty"
        unoptimized={unoptimized}
        quality={ERROR_COUNT_TO_QUALITY[errorCount]}
        onLoad={(e) => {
          if (e.target instanceof HTMLImageElement) {
            const ratio = (e.target.naturalWidth / e.target.naturalHeight)
            setIsPortrait(ratio < 0.67)
          }
        }}
        onError={() => {
          // unoptimized = true. it means we use Cloudfront url which doesn't rely on quality parameter. so we will let it fails fast
          setErrorCount((prev) => {
            return unoptimized ? prev + 99 : prev + 1
          });
        }}
      />
    </picture>
  );
};

export default ImageWithFallback;
